/**
 * ET - PDP Main Product (sections/et-pdp-main-product.liquid)
 * - Description truncation with an inline "Show more" / "Show less" toggle
 * - Add-ons (SD cards): select / deselect, keyboard support, added in the SAME cart/add.js request as the product.
 *   Impact's product-form builds `new FormData(form)` synchronously when it submits; the `formdata` event lets us rewrite
 *   that FormData into items[] so the theme flow (variant:add, cart:change, ET cart drawer) stays untouched.
 *   Single choice (role=radio) is one Tab stop with a roving tabindex; arrow keys move the selection like a radio group.
 * - Cart drawer: after an add from this section the cart drawer (#cart-drawer) opens even when the theme cart type is
 *   'popover' (section setting open_cart_drawer), instead of Impact's notification popover.
 * - Mobile quantity dropdown kept in sync with the theme quantity input
 * - Quantity offers (<et-quantity-offers>, snippets/et-pdp-main-product-quantity-offers.liquid): the chosen option's
 *   quantity is written into the product form's quantity input(s), and with data-hide-quantity the quantity selector is
 *   hidden while an offer (quantity above 1) is chosen. The cart price comes from the store's automatic discount.
 * - Variant re-render: refreshes the Klarna block and gallery badges from the re-rendered markup
 * - Sticky add to cart bar: re-checks Impact's visibility rule once scrolling settles (see syncStickyBars)
 * - Gallery autoplay (section settings gallery_autoplay / gallery_autoplay_speed): advances Impact's <media-carousel>
 *   while it is in view, pauses on hover / focus / hidden tab, stops after the first interaction with the gallery
 * All listeners are delegated on document, so theme editor section reloads need no re-binding.
 */
(() => {
  if (window.etPdpMainProduct) {
    return;
  }

  const SECTION_SELECTOR = '.et-pdp-main-product';
  const MOBILE_QUERY = window.matchMedia('(max-width: 749px)');

  /* ---------------------------------------------------------------------------------------------------------------- */
  /* Description truncation                                                                                           */
  /* ---------------------------------------------------------------------------------------------------------------- */

  const fitDescription = (root) => {
    const collapsed = root.querySelector('[data-et-truncate-collapsed]');
    const text = root.querySelector('[data-et-truncate-text]');
    const toggle = collapsed?.querySelector('[data-et-truncate-toggle]');

    if (!collapsed || !text || collapsed.hidden || collapsed.getClientRects().length === 0) {
      return;
    }

    if (root.etFullText === undefined) {
      root.etFullText = text.textContent.trim();
    }

    const fullText = root.etFullText;
    const lines = parseInt(MOBILE_QUERY.matches ? root.dataset.linesMobile : root.dataset.lines, 10) || 3;
    const lineHeight = parseFloat(getComputedStyle(collapsed).lineHeight) || 24;
    const maxHeight = lineHeight * lines + 1;

    collapsed.style.maxHeight = 'none';
    text.textContent = fullText;

    if (toggle) {
      toggle.hidden = false;
    }

    if (collapsed.scrollHeight <= maxHeight) {
      // Everything fits: no toggle needed.
      if (toggle) {
        toggle.hidden = true;
      }
    } else {
      const words = fullText.split(/\s+/);
      let low = 0;
      let high = words.length;

      while (low < high) {
        const middle = Math.ceil((low + high) / 2);
        text.textContent = `${words.slice(0, middle).join(' ')}…`;

        if (collapsed.scrollHeight <= maxHeight) {
          low = middle;
        } else {
          high = middle - 1;
        }
      }

      text.textContent = `${words.slice(0, low).join(' ')}…`;
    }

    collapsed.style.maxHeight = '';
    root.classList.add('is-fitted');
  };

  const fitAllDescriptions = (scope = document) => {
    const selector = scope === document ? `${SECTION_SELECTOR} [data-et-truncate]` : '[data-et-truncate]';
    scope.querySelectorAll(selector).forEach(fitDescription);
  };

  const toggleDescription = (button) => {
    const root = button.closest('[data-et-truncate]');

    if (!root) {
      return;
    }

    const expand = button.dataset.etTruncateToggle === 'more';
    const collapsed = root.querySelector('[data-et-truncate-collapsed]');
    const full = root.querySelector('[data-et-truncate-full]');

    collapsed.hidden = expand;
    full.hidden = !expand;
    root.classList.toggle('is-expanded', expand);

    if (!expand) {
      fitDescription(root);
    }

    const target = root.querySelector(`[data-et-truncate-toggle="${expand ? 'less' : 'more'}"]`);

    if (target && !target.hidden) {
      target.focus({ preventScroll: expand });
    }
  };

  /* ---------------------------------------------------------------------------------------------------------------- */
  /* Add-ons                                                                                                          */
  /* ---------------------------------------------------------------------------------------------------------------- */

  const isSingleChoice = (group) => group.dataset.multiple !== 'true';

  // Radio mode: the checked add-on is the group's only Tab stop; with nothing checked the tab stop stays on `preferred`
  // (the option just used) or on the option that already had it.
  const syncAddonTabStops = (group, preferred = null) => {
    if (!isSingleChoice(group)) {
      return;
    }

    const options = Array.from(group.querySelectorAll('[data-et-addon]'));
    const enabled = options.filter((item) => !item.disabled);
    const tabStop =
      enabled.find((item) => item.getAttribute('aria-checked') === 'true') ||
      (preferred && enabled.includes(preferred) ? preferred : null) ||
      enabled.find((item) => item.getAttribute('tabindex') === '0') ||
      enabled[0];

    options.forEach((item) => item.setAttribute('tabindex', item === tabStop ? '0' : '-1'));
  };

  const selectAddon = (option) => {
    const group = option.closest('[data-et-addons]');

    if (!group || option.disabled) {
      return;
    }

    const checked = option.getAttribute('aria-checked') === 'true';

    if (isSingleChoice(group)) {
      group.querySelectorAll('[data-et-addon]').forEach((item) => {
        if (item !== option) {
          item.setAttribute('aria-checked', 'false');
        }
      });
    }

    option.setAttribute('aria-checked', checked ? 'false' : 'true');
    syncAddonTabStops(group, option);
  };

  // Radio mode keyboard (WAI-ARIA radio group): arrows / Home / End move focus AND selection. Space / Enter still toggle
  // through the native button click, so the optional add-on can be deselected again.
  const moveAddonSelection = (option, key) => {
    const group = option.closest('[data-et-addons]');

    if (!group || !isSingleChoice(group)) {
      return false;
    }

    const options = Array.from(group.querySelectorAll('[data-et-addon]:not(:disabled)'));
    const index = options.indexOf(option);

    if (index === -1) {
      return false;
    }

    if (options.length < 2) {
      // A lone option keeps its state (Space / Enter toggle it); the arrow key is still consumed like in a radio group.
      return true;
    }

    let next;

    if (key === 'Home') {
      next = options[0];
    } else if (key === 'End') {
      next = options[options.length - 1];
    } else {
      const direction = key === 'ArrowRight' || key === 'ArrowDown' ? 1 : -1;
      next = options[(index + direction + options.length) % options.length];
    }

    group.querySelectorAll('[data-et-addon]').forEach((item) => {
      item.setAttribute('aria-checked', item === next ? 'true' : 'false');
    });

    syncAddonTabStops(group, next);
    next.focus();
    return true;
  };

  const selectedAddonIds = (form) => {
    const section = form.closest(SECTION_SELECTOR);

    if (!section) {
      return [];
    }

    return Array.from(section.querySelectorAll('[data-et-addons] [data-et-addon][aria-checked="true"]:not(:disabled)'))
      .map((option) => option.dataset.variantId)
      .filter(Boolean);
  };

  let pendingForm = null;

  const onSubmitCapture = (event) => {
    const form = event.target;

    if (!(form instanceof HTMLFormElement) || !form.closest(SECTION_SELECTOR) || !form.matches('[is="product-form"]')) {
      return;
    }

    pendingForm = form;

    // The theme builds its FormData synchronously inside the same submit dispatch. Clear afterwards so an unrelated
    // FormData built later from this form (e.g. a blocked double submit) is never rewritten.
    setTimeout(() => {
      if (pendingForm === form) {
        pendingForm = null;
      }
    }, 0);
  };

  const onFormData = (event) => {
    const form = event.target;

    if (!pendingForm || form !== pendingForm) {
      return;
    }

    pendingForm = null;

    const addonIds = selectedAddonIds(form);
    const formData = event.formData;

    if (addonIds.length === 0 || !formData.get('id')) {
      return;
    }

    const lineEntries = [];

    for (const [key, value] of formData.entries()) {
      if (key === 'id' || key === 'quantity' || key === 'selling_plan' || key.startsWith('properties[')) {
        lineEntries.push([key, value]);
      }
    }

    lineEntries.forEach(([key]) => formData.delete(key));

    lineEntries.forEach(([key, value]) => {
      const itemKey = key.startsWith('properties[') ? `items[0][properties]${key.slice('properties'.length)}` : `items[0][${key}]`;
      formData.append(itemKey, value);
    });

    if (!formData.has('items[0][quantity]')) {
      formData.append('items[0][quantity]', '1');
    }

    addonIds.forEach((variantId, index) => {
      formData.append(`items[${index + 1}][id]`, variantId);
      formData.append(`items[${index + 1}][quantity]`, '1');
    });
  };

  /* ---------------------------------------------------------------------------------------------------------------- */
  /* Cart drawer after add                                                                                            */
  /* ---------------------------------------------------------------------------------------------------------------- */

  // Impact opens #cart-drawer on variant:add only when the theme cart type is 'drawer'; with 'popover' it opens its
  // notification popover, which lists only the main product (not the add-ons added in the same request). Runs in the
  // capture phase, before the theme's document listeners: the theme's own `blockCartDrawerOpening` flag stops the
  // popover, then the drawer opens and is filled by the cart:change event that the product form dispatches right after.
  const onVariantAdd = (event) => {
    const form = event.target;
    const detail = event.detail;

    if (
      !(form instanceof HTMLFormElement) ||
      !detail ||
      detail.blockCartDrawerOpening ||
      !form.closest(`${SECTION_SELECTOR} [data-et-open-cart-drawer]`) ||
      form.closest('quick-buy-drawer, quick-buy-popover')
    ) {
      return;
    }

    const cartType = window.themeVariables?.settings?.cartType;

    if (cartType === 'drawer' || cartType === 'page') {
      return;
    }

    const drawer = document.getElementById('cart-drawer');

    if (!drawer || typeof drawer.show !== 'function') {
      return;
    }

    detail.blockCartDrawerOpening = true;
    drawer.show();
  };

  /* ---------------------------------------------------------------------------------------------------------------- */
  /* Mobile quantity dropdown                                                                                         */
  /* ---------------------------------------------------------------------------------------------------------------- */

  const syncQuantitySelect = (input) => {
    const select = input.closest('quantity-selector')?.querySelector('[data-et-quantity-select]');

    if (!select || input.value === '') {
      return;
    }

    const value = String(parseInt(input.value, 10) || 1);

    if (!Array.from(select.options).some((option) => option.value === value)) {
      select.add(new Option(value, value));
    }

    select.value = value;
  };

  /* Mobile: the transparent native select is the quantity control and the number input only shows the value, so the
     input leaves the tab order and the accessibility tree there (one control instead of two). Desktop restores it. */
  const syncQuantityInputAccess = (root = document) => {
    root.querySelectorAll?.(`${SECTION_SELECTOR} quantity-selector input`).forEach((input) => {
      if (!input.closest('quantity-selector')?.querySelector('[data-et-quantity-select]')) {
        return;
      }

      if (MOBILE_QUERY.matches) {
        input.setAttribute('tabindex', '-1');
        input.setAttribute('aria-hidden', 'true');
      } else {
        input.removeAttribute('tabindex');
        input.removeAttribute('aria-hidden');
      }
    });
  };

  const onQuantitySelectChange = (select) => {
    const input = select.closest('quantity-selector')?.querySelector('input');

    if (!input) {
      return;
    }

    input.value = select.value;
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  };

  /* ---------------------------------------------------------------------------------------------------------------- */
  /* Quantity offers                                                                                                  */
  /* ---------------------------------------------------------------------------------------------------------------- */

  const applyQuantityOffer = (offers) => {
    const checked = offers.querySelector('input[type="radio"]:checked');
    const formId = offers.dataset.form;

    if (!checked || !formId) {
      return;
    }

    const quantity = parseInt(checked.value, 10) || 1;
    const escapedId = CSS.escape(formId);
    const inputs = document.querySelectorAll(`input[name="quantity"][form="${escapedId}"], #${escapedId} input[name="quantity"]`);

    inputs.forEach((input) => {
      if (input.value === String(quantity)) {
        return;
      }

      input.value = String(quantity);
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });

    const section = offers.closest(SECTION_SELECTOR);

    if (section && offers.dataset.hideQuantity === 'true') {
      section.querySelectorAll('.et-pdp-main-product__info [data-block-type="quantity-selector"]').forEach((item) => {
        item.hidden = quantity > 1;
      });
    }
  };

  const applyAllQuantityOffers = (root = document) => {
    root.querySelectorAll?.(`${SECTION_SELECTOR} et-quantity-offers`).forEach(applyQuantityOffer);
  };

  /* ---------------------------------------------------------------------------------------------------------------- */
  /* Variant re-render (multi-variant products)                                                                       */
  /* ---------------------------------------------------------------------------------------------------------------- */

  const findInFragment = (root, selector) => {
    let element = root.querySelector(selector);

    if (element) {
      return element;
    }

    for (const template of root.querySelectorAll('template')) {
      element = findInFragment(template.content, selector);

      if (element) {
        return element;
      }
    }

    return null;
  };

  const onProductRerender = (event) => {
    const form = event.target;
    const fragment = event.detail?.htmlFragment;
    const section = form instanceof Element ? form.closest(SECTION_SELECTOR) : null;

    if (section) {
      setTimeout(() => {
        initGalleryAutoplay(section);
        syncQuantityInputAccess();
        applyAllQuantityOffers(section);
      }, 0);
    }

    if (!section || !fragment || event.detail.productChange) {
      if (section && event.detail?.productChange) {
        setTimeout(() => fitAllDescriptions(section), 0);
      }
      return;
    }

    section.querySelectorAll('[data-block-type="et-klarna"][data-block-id]').forEach((block) => {
      const match = findInFragment(fragment, `[data-block-type="et-klarna"][data-block-id="${block.dataset.blockId}"]`);

      if (match) {
        block.innerHTML = match.innerHTML;
      }
    });

    const badges = section.querySelector('.et-pdp-main-product__media-wrapper .et-pdp-main-product__badges');
    const newBadges = findInFragment(fragment, '.et-pdp-main-product__media-wrapper .et-pdp-main-product__badges');

    if (badges && newBadges) {
      badges.replaceWith(newBadges.cloneNode(true));
    } else if (badges && !newBadges) {
      badges.remove();
    } else if (!badges && newBadges) {
      section.querySelector('.et-pdp-main-product__media-wrapper')?.prepend(newBadges.cloneNode(true));
    }
  };

  /* ---------------------------------------------------------------------------------------------------------------- */
  /* Sticky add to cart bar                                                                                           */
  /* ---------------------------------------------------------------------------------------------------------------- */

  // Impact's <product-quick-add> only updates on IntersectionObserver threshold crossings. A jump scroll from the
  // footer back above the product form (back to top, anchor links) crosses no threshold for the form, so the bar stays
  // visible over the page top. Same rule as Impact (form scrolled past and footer not in view), from live geometry.
  const syncStickyBars = () => {
    const footer = document.querySelector('.footer');

    if (!footer) {
      return;
    }

    document.querySelectorAll(`${SECTION_SELECTOR} product-quick-add[form]`).forEach((bar) => {
      const form = document.getElementById(bar.getAttribute('form'));

      if (!form) {
        return;
      }

      const footerRect = footer.getBoundingClientRect();
      const formPassed = form.getBoundingClientRect().bottom < 0;
      const footerReached = footerRect.top < window.innerHeight && footerRect.bottom > 0;

      bar.classList.toggle('is-visible', formPassed && !footerReached);
    });
  };

  let stickyTimer = null;

  const scheduleStickySync = () => {
    clearTimeout(stickyTimer);
    stickyTimer = setTimeout(syncStickyBars, 150);
  };

  /* ---------------------------------------------------------------------------------------------------------------- */
  /* Gallery autoplay                                                                                                 */
  /* ---------------------------------------------------------------------------------------------------------------- */

  const AUTOPLAY_SELECTOR = 'media-carousel[data-et-autoplay]';
  const REDUCED_MOTION_QUERY = window.matchMedia('(prefers-reduced-motion: reduce)');
  const autoplayTimers = new Map();

  const stopGalleryAutoplay = (carousel) => {
    clearInterval(autoplayTimers.get(carousel));
    autoplayTimers.delete(carousel);
  };

  const advanceGallery = (carousel) => {
    if (!carousel.isConnected) {
      stopGalleryAutoplay(carousel);
      return;
    }

    const gallery = carousel.closest('product-gallery') || carousel;
    const rect = carousel.getBoundingClientRect();

    if (
      document.hidden ||
      gallery.matches(':hover') ||
      gallery.matches(':focus-within') ||
      rect.width === 0 ||
      rect.bottom <= 0 ||
      rect.top >= window.innerHeight ||
      typeof carousel.select !== 'function'
    ) {
      return;
    }

    const visible = carousel.visibleItems || [];

    if (visible.length < 2) {
      return;
    }

    const current = visible.indexOf(carousel.selectedSlide);
    const next = visible[(current + 1) % visible.length];

    carousel.select(carousel.items.indexOf(next));
  };

  const initGalleryAutoplay = (scope = document) => {
    autoplayTimers.forEach((timer, carousel) => {
      if (!carousel.isConnected) {
        stopGalleryAutoplay(carousel);
      }
    });

    if (REDUCED_MOTION_QUERY.matches) {
      return;
    }

    const selector = scope === document ? `${SECTION_SELECTOR} ${AUTOPLAY_SELECTOR}` : AUTOPLAY_SELECTOR;

    scope.querySelectorAll(selector).forEach((carousel) => {
      if (autoplayTimers.has(carousel) || carousel.etAutoplayStopped) {
        return;
      }

      const seconds = Math.max(parseInt(carousel.dataset.etAutoplay, 10) || 5, 1);
      autoplayTimers.set(carousel, setInterval(() => advanceGallery(carousel), seconds * 1000));
    });
  };

  // The first swipe, click or key press in the gallery hands control to the customer for the rest of the visit.
  const onGalleryInteraction = (event) => {
    const gallery = event.target.closest?.(`${SECTION_SELECTOR} product-gallery`);

    if (!gallery) {
      return;
    }

    gallery.querySelectorAll(AUTOPLAY_SELECTOR).forEach((carousel) => {
      carousel.etAutoplayStopped = true;
      stopGalleryAutoplay(carousel);
    });
  };

  /* ---------------------------------------------------------------------------------------------------------------- */
  /* Listeners                                                                                                        */
  /* ---------------------------------------------------------------------------------------------------------------- */

  document.addEventListener('click', (event) => {
    const toggle = event.target.closest?.(`${SECTION_SELECTOR} [data-et-truncate-toggle]`);

    if (toggle) {
      toggleDescription(toggle);
      return;
    }

    const addon = event.target.closest?.(`${SECTION_SELECTOR} [data-et-addon]`);

    if (addon) {
      selectAddon(addon);
    }
  });

  document.addEventListener('keydown', (event) => {
    if (!['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
      return;
    }

    if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) {
      return;
    }

    const addon = event.target.closest?.(`${SECTION_SELECTOR} [data-et-addon]`);

    if (addon && moveAddonSelection(addon, event.key)) {
      event.preventDefault();
    }
  });

  document.addEventListener('change', (event) => {
    const target = event.target;

    if (!(target instanceof Element) || !target.closest(SECTION_SELECTOR)) {
      return;
    }

    if (target.matches('[data-et-quantity-select]')) {
      onQuantitySelectChange(target);
    } else if (target.matches('quantity-selector input')) {
      syncQuantitySelect(target);
    } else if (target.matches('et-quantity-offers input[type="radio"]')) {
      applyQuantityOffer(target.closest('et-quantity-offers'));
    }
  });

  document.addEventListener('input', (event) => {
    if (event.target instanceof Element && event.target.matches(`${SECTION_SELECTOR} quantity-selector input`)) {
      syncQuantitySelect(event.target);
    }
  });

  document.addEventListener('pointerdown', onGalleryInteraction, true);
  document.addEventListener('keydown', onGalleryInteraction, true);
  document.addEventListener('submit', onSubmitCapture, true);
  document.addEventListener('variant:add', onVariantAdd, true);
  document.addEventListener('formdata', onFormData, true);
  document.addEventListener('product:rerender', onProductRerender, true);

  let lastWidth = window.innerWidth;
  let resizeFrame = null;

  window.addEventListener('resize', () => {
    if (window.innerWidth === lastWidth) {
      return;
    }

    lastWidth = window.innerWidth;
    scheduleStickySync();
    cancelAnimationFrame(resizeFrame);
    resizeFrame = requestAnimationFrame(() => fitAllDescriptions());
  });

  MOBILE_QUERY.addEventListener('change', () => {
    fitAllDescriptions();
    syncQuantityInputAccess();
  });

  window.addEventListener('scroll', scheduleStickySync, { passive: true });

  /* Back / forward: a page restored from the back-forward cache keeps its radios but runs no init, so re-apply the
     checked offer (the radios carry autocomplete="off", so a reload never restores a stale choice either). */
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) {
      applyAllQuantityOffers();
    }
  });

  document.addEventListener('shopify:section:load', (event) => {
    if (event.target.matches?.(SECTION_SELECTOR) || event.target.querySelector?.(SECTION_SELECTOR)) {
      fitAllDescriptions(event.target);
      initGalleryAutoplay(event.target);
      syncQuantityInputAccess(event.target);
      applyAllQuantityOffers(event.target);
    }
  });

  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll?.(AUTOPLAY_SELECTOR).forEach(stopGalleryAutoplay);
  });

  const init = () => {
    fitAllDescriptions();
    scheduleStickySync();
    initGalleryAutoplay();
    syncQuantityInputAccess();
    applyAllQuantityOffers();

    if (document.fonts?.ready) {
      document.fonts.ready.then(() => fitAllDescriptions());
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.etPdpMainProduct = { fitAllDescriptions, syncStickyBars, initGalleryAutoplay };
})();
